import { useCallback, useMemo } from 'react'
import { collection, query, serverTimestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { reportSendFailedToast } from '../lib/commonStrings'
import { addDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { useFirestoreCollection } from './useFirestore'
import type { SetReport, SetReportCategory } from '../types'

// A user flagging something wrong on one set. Clients only ever create — the
// schedule-leads routine records its verdict and triage happens in the admin queue.
export function useSetReports(
  set: { id: string; eventId: string; artistIds: string[] } | null,
  userId: string | null,
) {
  const setId = set?.id ?? null
  // Scoped to the viewer's own reports on this set, so the dialog can say which
  // problems they've already flagged instead of taking the same one twice.
  const myReportsQuery = useMemo(
    () =>
      userId && setId
        ? (query(
            collection(db, 'setReports'),
            where('submittedBy', '==', userId),
            where('setId', '==', setId),
          ) as Query<SetReport>)
        : null,
    [userId, setId],
  )

  const { data } = useFirestoreCollection<SetReport>(myReportsQuery)

  const pendingCategories = useMemo(
    () => new Set<SetReportCategory>(data.filter(r => r.status === 'pending').map(r => r.category)),
    [data],
  )

  const submitReport = useCallback(
    async (category: SetReportCategory, message: string) => {
      if (!userId || !set) return
      const trimmed = message.trim()
      await runMutation(
        reportSendFailedToast(),
        async () => {
          await addDoc(collection(db, 'setReports'), {
            eventId: set.eventId,
            setId: set.id,
            artistIds: set.artistIds,
            category,
            ...(trimmed ? { message: trimmed } : {}),
            submittedBy: userId,
            createdAt: serverTimestamp(),
            status: 'pending',
          })
          track('set_report_submitted', { event_id: set.eventId, kind: category })
        },
      )
    },
    [userId, set],
  )

  return { submitReport, pendingCategories }
}
